/**
 * Copy text to the clipboard, truthfully (D-108).
 *
 * `navigator.clipboard` is the happy path, but it is not universal: it is missing
 * on insecure origins and some in-app browsers, and it rejects when the page is not
 * focused or the platform wants a different gesture. A 2FA setup key is exactly the
 * string a phone user has to move between two apps, so a silent failure ("Copied"
 * when nothing was copied) is worse than no button at all — this returns whether it
 * actually worked and the caller says so.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // Fall through to the legacy path below.
  }

  // Legacy fallback: a hidden, read-only textarea plus execCommand('copy'). Still
  // the only option in some Android WebViews and old Safari.
  try {
    const area = document.createElement('textarea')
    area.value = text
    area.setAttribute('readonly', '')
    area.style.position = 'fixed'
    area.style.top = '0'
    area.style.left = '0'
    area.style.opacity = '0'
    document.body.appendChild(area)
    area.select()
    area.setSelectionRange(0, text.length)
    const copied = document.execCommand('copy')
    document.body.removeChild(area)
    return copied
  } catch {
    return false
  }
}
